import { Pagination } from '@packages/toolbox/dist/declarations/pagination';
import { AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import { http } from 'SharedDir/backend/axios/http';
import SimpleApi from 'SharedDir/backend/api/classes/simpleApi';
import {
  BASE_PROFILE_TYPE_URL,
  PROFILE_TYPE_ENDPOINTS,
} from 'SharedDir/backend/endpoints/profile-types';
import { StatusResponse } from 'SharedDir/declarations/endpoint';
import { ProfileType } from 'SharedDir/declarations/profile-types';
import { Section, SectionsList } from 'SharedDir/declarations/section-field';
import { NotNull, Nullable, WithRelations } from 'SharedDir/declarations/shared';
import cloneDeep from 'lodash/cloneDeep';
import { Paginated, PaginationParams } from 'SharedDir/declarations/pagination';

import { paramsAllRequest } from '../default-params';

type SectionsResponse = PaginationParams<{
  data: SectionsList;
}>;

type TSingle = ProfileType<WithRelations<Required<NotNull<Section, 'id'>>>>;
type TPaginated = TSingle;

export default class ProfileTypeApiClass {
  protected url: string;

  constructor() {
    this.url = BASE_PROFILE_TYPE_URL;
  }

  /**
   * Запрашиваем все!!!! записи
   *
   * @param {Omit<PaginationParams<Extra>, "rowsPerPage" | "page">} params
   * @returns {Promise<any>}
   */
  public async getAll<Extra extends object>(
    params: Omit<PaginationParams<object>, 'rowsPerPage' | 'page'> & Extra
  ) {
    const { data } = await this.getPaginated({ ...params, rowsPerPage: 0, page: 1 });
    return data;
  }

  /**
   * Постраничный запрос элемнатов
   * @param params
   */
  public async getPaginated<Extra extends object>(params: PaginationParams<Extra>) {
    const endpoint = this.getPaginationEndpoint();
    const { data } = (await http({ ...endpoint, params })) as AxiosResponse<
      Paginated<TPaginated, Extra>
    >;
    return data;
  }

  /**
   * Запрашиваем элемент по идентификатору.
   *
   * Поддерживаются как числовые так и текстовые идентификаторы
   * @param id
   */
  public async get(id: string | number) {
    const endpoint = this.getSingleEndpoint(id);
    const { data } = (await http(endpoint)) as AxiosResponse<TSingle>;

    return data;
  }

  /**
   * Получаем все опубликованные типы профилей
   *
   * @static
   * @returns {Promise<Array<ProfileType>>}
   * @memberof ProfileTypeApi
   */
  async getAllPublished(): Promise<Array<ProfileType>> {
    return this.getAll({
      page: 1,
      sortBy: 'name',
      rowsPerPage: null,
      isPublished: true,
      descending: false,
    });
  }

  /**
   * Получаем все секции типа профиля
   * @static
   * @returns {Promise<Array<ProfileType>>}
   * @memberof ProfileTypeApi
   */
  // eslint-disable-next-line class-methods-use-this
  async getSections(
    profileTypeId: number,
    pagination = paramsAllRequest
  ): Promise<SectionsResponse> {
    const paginationData = cloneDeep(pagination);
    paginationData.sortBy = 'order';

    const { data }: AxiosResponse<SectionsResponse> = await http({
      ...PROFILE_TYPE_ENDPOINTS.SECTIONS.LIST,
      params: { ...paginationData, profileTypeId },
    });

    return data;
  }

  private getPaginationEndpoint(): AxiosRequestConfig {
    return { url: this.url, method: 'get' };
  }

  private getSingleEndpoint(id: string | number): AxiosRequestConfig {
    const url = `${this.url}/${id}`;
    return { url, method: 'get' };
  }
}
